import {
  getNewBundleId,
  getNewPageId,
} from '@/modules/presentation/clientPresentation/ClientPresentationDesigner.utils';
import {
  buildWaterfallPageItems,
  getWaterfallPageDefinitions,
  WaterfallConfigKey,
  WaterfallPageItem,
  WaterfallSubItemConfigKey,
} from '@/modules/presentation/components/WaterfallPageReorderList';
import { PresentationBundleKind } from '@/types/schema';

import { getCarriedOverBundleFields } from '../../../PresentationTemplateEditor.bundleFields';
import {
  PresentationTemplateEditorBundle,
  PresentationTemplateEditorPage,
} from '../../../PresentationTemplateEditor.types';

export type { WaterfallPageItem };

export interface WaterfallBundleConfigFormShape {
  pages: WaterfallPageItem[];
  templateNotes: string;
}

/**
 * Returns the pages and Summary options for the modal.
 *
 * Templates do not store the summary page mode. A presentation made from a
 * template uses the flag state at creation, so the modal shows the Summary
 * options of the current flag state.
 */
export function getDefaultWaterfallPages(
  bundle: PresentationTemplateEditorBundle | undefined,
  useUpdatedSummaryPage: boolean
): WaterfallPageItem[] {
  // A template belongs to no household, so it keeps the second-death option.
  const pageDefinitions = getWaterfallPageDefinitions({
    useUpdatedSummaryPage,
  });

  if (!bundle) {
    // Create mode: all checked, default order. Pass an empty config so
    // buildWaterfallPageItems hydrates sub-items with their default (true) state.
    return buildWaterfallPageItems(
      {
        includeCoverPage: true,
        includeAssumptionsPage: true,
        includeSummaryPage: true,
        includeDiagramPage: true,
        includeTaxOverviewPage: true,
        includeSummaryBars: true,
        includeSummaryTiles: true,
        showSummaryTileDetails: true,
        includeOverview: true,
        includeBeforeFirstDeath: true,
        includeAfterFirstDeath: true,
        includeAfterSecondDeath: true,
      },
      [],
      pageDefinitions
    );
  }

  return buildWaterfallPageItems(
    bundle.waterfallTemplateBundleConfiguration,
    bundle.pages.map((p) => p.kind),
    pageDefinitions
  );
}

/**
 * Builds a PresentationTemplateEditorBundle from the modal's form values.
 *
 * The bundle is rebuilt from scratch, so bundle-level fields the modal doesn't
 * own come across via `getCarriedOverBundleFields`. `templateNotes` is the
 * exception — this modal edits it, so the form value wins.
 */
export function buildWaterfallBundleFromForm(
  pages: WaterfallPageItem[],
  templateNotes: string,
  existingBundle?: PresentationTemplateEditorBundle
): PresentationTemplateEditorBundle {
  const checkedPages = pages.filter((p) => p.checked);

  const bundlePages: PresentationTemplateEditorPage[] = checkedPages.map(
    (item) => ({
      id: getNewPageId(item.pageKind),
      kind: item.pageKind,
    })
  );

  // Build the config flags from checked state
  const configFlags: Record<WaterfallConfigKey, boolean> = {
    includeCoverPage: false,
    includeAssumptionsPage: false,
    includeSummaryPage: false,
    includeDiagramPage: false,
    includeTaxOverviewPage: false,
  };

  // The form holds only the Summary options of the current flag state. Collect
  // them, then keep the saved value of each option that the form does not show.
  const summaryFlags: Partial<Record<WaterfallSubItemConfigKey, boolean>> = {};

  for (const page of pages) {
    if (page.configKey) {
      configFlags[page.configKey] = page.checked;
    }
    // Persist sub-item flags (e.g. includeSummaryBars / includeSummaryTiles).
    // Sub-items track their own checked state; the parent is true whenever
    // any sub-item is checked (indeterminate counts as true).
    if (page.subItems) {
      for (const subItem of page.subItems) {
        summaryFlags[subItem.configKey] = subItem.checked;
      }
    }
  }

  const existingConfig = existingBundle?.waterfallTemplateBundleConfiguration;
  const getSummaryFlag = (
    key: WaterfallSubItemConfigKey,
    defaultValue = true
  ): boolean => summaryFlags[key] ?? existingConfig?.[key] ?? defaultValue;

  return {
    ...getCarriedOverBundleFields(existingBundle),
    id:
      existingBundle?.id ??
      getNewBundleId(PresentationBundleKind.WaterfallOverviewBundle),
    kind: PresentationBundleKind.WaterfallOverviewBundle,
    // This modal owns the field, so the form value overrides the carried one.
    templateNotes,
    waterfallTemplateBundleConfiguration: {
      __typename: 'WaterfallTemplateBundleConfiguration' as const,
      id: '',
      ...configFlags,
      includeSummaryBars: getSummaryFlag('includeSummaryBars'),
      includeSummaryTiles: getSummaryFlag('includeSummaryTiles'),
      showSummaryTileDetails: getSummaryFlag('showSummaryTileDetails'),
      includeOverview: getSummaryFlag('includeOverview'),
      includeBeforeFirstDeath: getSummaryFlag('includeBeforeFirstDeath'),
      includeAfterFirstDeath: getSummaryFlag('includeAfterFirstDeath'),
      includeAfterSecondDeath: getSummaryFlag('includeAfterSecondDeath'),
      summaryHideZeroDollarItems: getSummaryFlag(
        'summaryHideZeroDollarItems',
        false
      ),
    },
    pages: bundlePages,
  };
}
